import React from 'react';
import type { PortableTextComponents, PortableTextComponentProps, PortableTextMarkComponentProps } from '@portabletext/react';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';
import PlayerContainer from '@jwp/ott-ui-react/src/containers/PlayerContainer/PlayerContainer';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { buildSanityImageUrl } from '@jwp/ott-hooks-react/src/cms/utils/imageUtils';

import styles from './PortableTextComponents.module.scss';

// JWP Video Player Component
const JWPVideoPlayer: React.FC<{ jwpMediaId: string }> = ({ jwpMediaId }) => {
  const { isLoading, isError, data: item } = useMedia(jwpMediaId);

  if (isLoading) {
    return <div className={styles.videoLoading}>Loading video...</div>;
  }

  if (isError) {
    return <div className={styles.videoError}>Error loading video</div>;
  }

  return (
    <>
      {!isLoading && !isError && item && (item as any).mediaid && (
        <PlayerContainer isCustomPlayer item={item as PlaylistItem} feedId={item?.feedid} autostart={false} />
      )}
    </>
  );
};

export const portableTextComponents: PortableTextComponents = {
  types: {
    image: (props: PortableTextComponentProps<any>) => {
      const { value } = props;

      if (!value?.asset?._ref) {
        return null;
      }

      const imageUrl = buildSanityImageUrl(value.asset._ref, 800, undefined, 85);
      const alt = value.alt || value.caption || 'Article image';
      const caption = value.caption;

      return (
        <figure className={styles.articleImage}>
          <img src={imageUrl} alt={alt} loading="lazy" className={styles.image} />
          {caption && <figcaption className={styles.imageCaption}>{caption}</figcaption>}
        </figure>
      );
    },

    customImage: (props: PortableTextComponentProps<any>) => {
      const { value } = props;

      if (!value?.image?.asset?._ref) {
        return null;
      }

      const imageUrl = buildSanityImageUrl(value.image.asset._ref, 800, undefined, 85);
      const alt = value.alt || 'Article image';
      const caption = value.caption;

      // Determine size class based on size property
      const getSizeClass = (size: string): string => {
        switch (size) {
          case 'small':
            return styles.customImageSmall;
          case 'medium':
            return styles.customImageMedium;
          case 'large':
            return styles.customImageLarge;
          case 'full':
          default:
            return styles.customImageFull;
        }
      };

      // Determine alignment class
      const getAlignmentClass = (alignment: string): string => {
        switch (alignment) {
          case 'left':
            return styles.alignLeft;
          case 'right':
            return styles.alignRight;
          case 'center':
          default:
            return styles.alignCenter;
        }
      };

      // Determine caption position class
      const getCaptionPositionClass = (position: string): string => {
        switch (position) {
          case 'top':
            return styles.captionTop;
          case 'bottom':
            return styles.captionBottom;
          case 'left':
            return styles.captionLeft;
          case 'right':
            return styles.captionRight;
          default:
            return styles.captionBottom;
        }
      };

      // Build inline styles for custom dimensions and transforms
      const imageStyles: React.CSSProperties = {
        transform: `rotate(${value.rotation || 0}deg) scale(${(value.scale || 100) / 100})`,
        ...(value.widthPx && { width: `${value.widthPx}px` }),
        ...(value.heightPx && { height: `${value.heightPx}px` }),
        ...(value.lockAspect && { objectFit: 'cover' as const }),
      };

      const containerClass = `${styles.customImageContainer} ${getSizeClass(value.size)} ${getAlignmentClass(value.alignment)}`;
      const captionClass = `${styles.customImageCaption} ${getCaptionPositionClass(value.captionPosition)}`;

      // Handle different caption positions with different layouts
      if (value.captionPosition === 'left' || value.captionPosition === 'right') {
        return (
          <figure className={`${containerClass} ${styles.captionSide}`}>
            <div className={styles.customImageWrapper}>
              {value.captionPosition === 'left' && caption && (
                <figcaption className={captionClass}>
                  {caption.split('\n').map((line: string, index: number) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < caption.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </figcaption>
              )}
              <img src={imageUrl} alt={alt} loading="lazy" className={styles.customImage} style={imageStyles} />
              {value.captionPosition === 'right' && caption && (
                <figcaption className={captionClass}>
                  {caption.split('\n').map((line: string, index: number) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < caption.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </figcaption>
              )}
            </div>
          </figure>
        );
      }

      // Handle top/bottom caption positions
      return (
        <figure className={containerClass}>
          {value.captionPosition === 'top' && caption && (
            <figcaption className={captionClass}>
              {caption.split('\n').map((line: string, index: number) => (
                <React.Fragment key={index}>
                  {line}
                  {index < caption.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </figcaption>
          )}
          <img src={imageUrl} alt={alt} loading="lazy" className={styles.customImage} style={imageStyles} />
          {(value.captionPosition === 'bottom' || !value.captionPosition) && caption && (
            <figcaption className={captionClass}>
              {caption.split('\n').map((line: string, index: number) => (
                <React.Fragment key={index}>
                  {line}
                  {index < caption.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </figcaption>
          )}
        </figure>
      );
    },

    video: (props: PortableTextComponentProps<any>) => {
      const { value } = props;

      if (!value?.url) return null;

      return (
        <div className={styles.videoWrapper}>
          <video controls className={styles.video} poster={value.poster ? buildSanityImageUrl(value.poster.asset._ref, 800, 450, 85) : undefined}>
            <source src={value.url} type={value.mimeType || 'video/mp4'} />
            Your browser does not support the video tag.
          </video>
          {value.caption && <figcaption className={styles.videoCaption}>{value.caption}</figcaption>}
        </div>
      );
    },

    // New JWP Video type
    jwpVideo: (props: PortableTextComponentProps<any>) => {
      const { value } = props;

      if (!value?.jwpMediaId) {
        return null;
      }

      return <JWPVideoPlayer jwpMediaId={value.jwpMediaId} />;
    },

    code: (props: PortableTextComponentProps<any>) => {
      const { value } = props;

      return (
        <div className={styles.codeBlock}>
          {value.filename && <div className={styles.codeFilename}>{value.filename}</div>}
          <pre className={styles.codeWrapper}>
            <code className={`${styles.code} language-${value.language || 'text'}`}>{value.code}</code>
          </pre>
        </div>
      );
    },
  },
  block: {
    normal: (props: PortableTextComponentProps<any>) => <p className={styles.paragraph}>{props.children}</p>,
    h1: (props: PortableTextComponentProps<any>) => <h1 className={styles.heading1}>{props.children}</h1>,
    h2: (props: PortableTextComponentProps<any>) => <h2 className={styles.heading2}>{props.children}</h2>,
    h3: (props: PortableTextComponentProps<any>) => <h3 className={styles.heading3}>{props.children}</h3>,
    h4: (props: PortableTextComponentProps<any>) => <h4 className={styles.heading4}>{props.children}</h4>,
    h5: (props: PortableTextComponentProps<any>) => <h5 className={styles.heading5}>{props.children}</h5>,
    h6: (props: PortableTextComponentProps<any>) => <h6 className={styles.heading6}>{props.children}</h6>,
    blockquote: (props: PortableTextComponentProps<any>) => <blockquote className={styles.blockquote}>{props.children}</blockquote>,
  },
  marks: {
    link: (props: PortableTextMarkComponentProps<any>) => {
      const href = props.value?.href || props.value?.selfHref;
      const target = props.value?.openInNewTab ? '_blank' : '_self';
      const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

      return (
        <a href={href} target={target} rel={rel} className={styles.link}>
          {props.children}
        </a>
      );
    },
    strong: (props: PortableTextMarkComponentProps<any>) => <strong className={styles.strong}>{props.children}</strong>,
    em: (props: PortableTextMarkComponentProps<any>) => <em className={styles.em}>{props.children}</em>,
    underline: (props: PortableTextMarkComponentProps<any>) => <u className={styles.underline}>{props.children}</u>,
    'strike-through': (props: PortableTextMarkComponentProps<any>) => <s className={styles.strikeThrough}>{props.children}</s>,
    code: (props: PortableTextMarkComponentProps<any>) => <code className={styles.inlineCode}>{props.children}</code>,
    highlight: (props: PortableTextMarkComponentProps<any>) => <mark className={styles.highlight}>{props.children}</mark>,
  },
  list: {
    bullet: (props: PortableTextComponentProps<any>) => <ul className={styles.bulletList}>{props.children}</ul>,
    number: (props: PortableTextComponentProps<any>) => <ol className={styles.numberList}>{props.children}</ol>,
  },
  listItem: {
    bullet: (props: PortableTextComponentProps<any>) => <li className={styles.listItem}>{props.children}</li>,
    number: (props: PortableTextComponentProps<any>) => <li className={styles.listItem}>{props.children}</li>,
  },
  unknownType: (props: PortableTextComponentProps<any>) => {
    console.warn('Unknown portable text type:', props.value._type);
    return null;
  },
};
